import React, { useState } from 'react';
import { Form, Input, Button, Card, Typography, Result, Alert, Select, Row, Col } from 'antd';
import { MailOutlined, ArrowLeftOutlined, CheckCircleOutlined, UserOutlined, LockOutlined, ShopOutlined, BankOutlined, EnvironmentOutlined, PhoneOutlined, GlobalOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import { AuthService, BrandSignupPayload } from '../services/authService';

const { Title, Text, Paragraph } = Typography;

const Register: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [email, setEmail] = useState('');
  const [brandName, setBrandName] = useState('');
  const [error, setError] = useState<string | null>(null);

  const onFinish = async (values: any) => {
    try {
      setLoading(true);
      setError(null);

      if (values.password !== values.confirmPassword) {
        throw new Error('Şifreler eşleşmiyor');
      }

      const payload: BrandSignupPayload = {
        email: values.email,
        password: values.password,
        name: values.name,
        brandName: values.brandName,
        legalName: values.legalName,
        taxNumber: values.taxNumber,
        taxOffice: values.taxOffice,
        companyType: values.companyType,
        websiteUrl: values.websiteUrl,
        phone: values.phone,
        billingEmail: values.billingEmail || values.email,
        billingAddress: {
          line1: values.billingAddressLine1,
          line2: values.billingAddressLine2,
          city: values.billingCity,
          district: values.billingDistrict,
          postalCode: values.billingPostalCode,
          country: values.billingCountry || 'TR',
        },
      };

      await AuthService.registerBrand(payload);
      setEmail(values.email);
      setBrandName(values.brandName);
      setEmailSent(true);
    } catch (err: any) {
      setError(err.message || 'Bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  if (emailSent) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)', padding: 20 }}>
        <Card style={{ width: 560, boxShadow: '0 10px 40px rgba(0,0,0,0.1)', borderRadius: 16 }}>
          <Result
            icon={<CheckCircleOutlined style={{ color: '#52c41a' }} />}
            title="Email Doğrulaması Gerekli"
            subTitle={
              <div>
                <Paragraph><strong>{email}</strong> adresine doğrulama linki gönderdik.</Paragraph>
                <Paragraph type="secondary">
                  <strong>{brandName}</strong> markası pending olarak oluşturuldu. 20 deneme kredisi, email doğrulaması tamamlandıktan sonra tanımlanacak.
                </Paragraph>
              </div>
            }
            extra={[
              <Link to="/login" key="login"><Button type="primary">Giriş Sayfasına Dön</Button></Link>,
            ]}
          />
          <Alert message="Kredi güvenliği" description="Email doğrulanmadan marka aktifleşmez ve deneme kredisi verilmez." type="info" showIcon />
        </Card>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)', padding: '32px 16px' }}>
      <Card style={{ maxWidth: 920, margin: '0 auto', boxShadow: '0 10px 40px rgba(0,0,0,0.1)', borderRadius: 16 }}>
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>🚀</div>
          <Title level={2} style={{ marginBottom: 8 }}>Marka Kaydı Oluştur</Title>
          <Text type="secondary">Ticari bilgilerinizi girin, emailinizi doğrulayın, 20 deneme kredisiyle başlayın.</Text>
        </div>

        {error && <Alert message={error} type="error" showIcon closable onClose={() => setError(null)} style={{ marginBottom: 24 }} />}

        <Form name="register-brand" onFinish={onFinish} autoComplete="off" layout="vertical" size="large" initialValues={{ companyType: 'limited', billingCountry: 'TR' }}>
          <Title level={4}>Yetkili Hesap Bilgileri</Title>
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item name="name" label="Ad Soyad" rules={[{ required: true, message: 'Ad soyad gerekli' }]}>
                <Input prefix={<UserOutlined />} placeholder="Adınız Soyadınız" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="email" label="Email" rules={[{ required: true, message: 'Email gerekli' }, { type: 'email', message: 'Geçerli email girin' }]}>
                <Input prefix={<MailOutlined />} placeholder="ornek@sirket.com" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="password" label="Şifre" rules={[{ required: true, message: 'Şifre gerekli' }, { min: 8, message: 'Şifre en az 8 karakter olmalı' }]}>
                <Input.Password prefix={<LockOutlined />} placeholder="Şifre" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="confirmPassword" label="Şifre Tekrar" rules={[{ required: true, message: 'Şifre tekrar gerekli' }]}>
                <Input.Password prefix={<LockOutlined />} placeholder="Şifre tekrar" />
              </Form.Item>
            </Col>
          </Row>

          <Title level={4}>Marka ve Ticari Bilgiler</Title>
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item name="brandName" label="Marka Adı" rules={[{ required: true, message: 'Marka adı gerekli' }]}>
                <Input prefix={<ShopOutlined />} placeholder="Marka adınız" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="legalName" label="Ticari Ünvan" rules={[{ required: true, message: 'Ticari ünvan gerekli' }]}>
                <Input prefix={<BankOutlined />} placeholder="Şirket resmi ünvanı" />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item name="taxNumber" label="Vergi No / VKN-TCKN" rules={[{ required: true, message: 'Vergi numarası gerekli' }, { pattern: /^\d{10,11}$/, message: '10 veya 11 haneli olmalı' }]}>
                <Input placeholder="1234567890" />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item name="taxOffice" label="Vergi Dairesi" rules={[{ required: true, message: 'Vergi dairesi gerekli' }]}>
                <Input placeholder="Kadıköy Vergi Dairesi" />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item name="companyType" label="Şirket Tipi" rules={[{ required: true, message: 'Şirket tipi gerekli' }]}>
                <Select options={[{ value: 'sole_proprietorship', label: 'Şahıs' }, { value: 'limited', label: 'Limited' }, { value: 'joint_stock', label: 'Anonim' }, { value: 'other', label: 'Diğer' }]} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="websiteUrl" label="Website URL" rules={[{ type: 'url', message: 'Geçerli URL girin' }]}>
                <Input prefix={<GlobalOutlined />} placeholder="https://www.markaniz.com" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="phone" label="Telefon">
                <Input prefix={<PhoneOutlined />} placeholder="+90..." />
              </Form.Item>
            </Col>
          </Row>

          <Title level={4}>Fatura Adresi</Title>
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item name="billingEmail" label="Fatura Emaili">
                <Input prefix={<MailOutlined />} placeholder="Boşsa hesap emaili kullanılır" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="billingCountry" label="Ülke" rules={[{ required: true }]}> <Input placeholder="TR" /> </Form.Item>
            </Col>
            <Col xs={24}>
              <Form.Item name="billingAddressLine1" label="Adres Satırı 1" rules={[{ required: true, message: 'Adres gerekli' }]}>
                <Input prefix={<EnvironmentOutlined />} placeholder="Mahalle, sokak, no" />
              </Form.Item>
            </Col>
            <Col xs={24}>
              <Form.Item name="billingAddressLine2" label="Adres Satırı 2">
                <Input placeholder="Kat, daire, ek bilgi" />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item name="billingCity" label="Şehir" rules={[{ required: true, message: 'Şehir gerekli' }]}>
                <Input placeholder="İstanbul" />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item name="billingDistrict" label="İlçe">
                <Input placeholder="Kadıköy" />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item name="billingPostalCode" label="Posta Kodu">
                <Input placeholder="34710" />
              </Form.Item>
            </Col>
          </Row>

          <Alert style={{ marginBottom: 24 }} type="info" showIcon message="Email doğrulaması zorunlu" description="20 deneme kredisi sadece email doğrulaması tamamlandıktan sonra tanımlanır." />

          <Form.Item>
            <Button type="primary" htmlType="submit" block loading={loading} style={{ height: 48, fontSize: 16, fontWeight: 600, background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)', border: 'none' }}>
              Kaydı Başlat ve Doğrulama Maili Gönder
            </Button>
          </Form.Item>

          <div style={{ textAlign: 'center', marginTop: 16 }}>
            <Link to="/login" style={{ color: '#11998e' }}><ArrowLeftOutlined style={{ marginRight: 8 }} />Giriş sayfasına dön</Link>
          </div>
        </Form>
      </Card>
    </div>
  );
};

export default Register;
